import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { isCampaignRoutingTargetUsable } from "@signalist/shared-types";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import { classNames } from "@/utils/classNames";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import { RoutingCampaign, targetNoteKeyOf, targetStateOf } from "./utils/campaignRoutingTargets";
import { RoutingTargetPill } from "./RoutingTargetPill";

interface RoutingCampaignPickerProps {
	campaignUuid: string | null;
	campaigns: readonly RoutingCampaign[];
	onChange: (campaignUuid: string) => void;
}

/** "THEN ASSIGN TO": this list's campaigns with their status, and what the chosen one means. */
export const RoutingCampaignPicker: FunctionComponent<RoutingCampaignPickerProps> = ({
	campaignUuid,
	campaigns,
	onChange,
}) => {
	const { t } = useTranslation();

	const options = useMemo(
		() =>
			campaigns.map((campaign) => ({
				id: campaign.uuid,
				title: campaign.name,
				icon: <RoutingTargetPill state={campaign.state} />,
			})),
		[campaigns]
	);
	const state = campaignUuid ? targetStateOf(campaignUuid, campaigns) : null;
	const noteKey = state ? targetNoteKeyOf(state) : null;
	const selected = options.find((option) => option.id === campaignUuid);

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center gap-2">
				<span className="text-xs font-semibold tracking-wide text-text-meta">
					{t(`${ROUTING_I18N}.editor.thenAssignTo`)}
				</span>
				<Dropdown
					floating
					portal
					data={options}
					defaultValue={selected}
					handleSelect={(item) => onChange(String(item.id))}
					testId="routing-campaign"
				/>
				{state && <RoutingTargetPill state={state} />}
			</div>
			{state && noteKey && (
				<p
					className={classNames(
						"text-xs",
						isCampaignRoutingTargetUsable(state) ? "text-text-meta" : "text-status-error"
					)}
					data-testid="routing-target-note"
				>
					{t(noteKey)}
				</p>
			)}
		</div>
	);
};
